// Cinematic auto-tour — Phase 2 polish.
//
// Press ▶ Tour cinemático → the camera self-walks through every room:
//   • Rotate 360° in the current room over ROTATION_DURATION_MS.
//   • Fade-cycle to the next room.
//   • Repeat until all rooms visited, then loop back to the first.
//
// Pairs with the AI narrator (see src/ui/narrator.ts): if the narrator
// is enabled when the tour starts, each room's `narration` is spoken as
// the camera arrives. Together they form a self-running demo movie —
// useful for unattended showings and trade-show kiosks.
//
// Any user input (pointer / wheel / key / touch) cancels the tour
// immediately so the operator can take back control.

import type { Room, RoomsConfig } from '../app/types';
import type { CameraControls } from '../playcanvas/cameraControls';
import { showToast } from './toast';

// Rotation needs to be long enough that an ~80-100 word pt-BR narration
// (which runs ~30-40s at default TTS speed) doesn't get clipped when the
// next fade-cycle starts. Bumped from 8s to 25s on 2026-05-15.
const ROTATION_DURATION_MS = 25_000; // a full 360° pan per room
const ROOM_DWELL_MS = 2_500;         // pause after rotation finishes
// Additional safety: AFTER rotation + dwell, also wait until the narrator
// has stopped speaking before advancing. Capped so we don't hang if speech
// synthesis gets wedged on some browsers.
const NARRATION_WAIT_POLL_MS = 250;
const NARRATION_WAIT_MAX_MS = 60_000;
const TOUR_STORAGE_KEY = 'imersivo:auto-tour-running';

export interface AutoTourDeps {
  config: RoomsConfig;
  controls: CameraControls;
  getCurrentRoom: () => Room | null;
  navigateToRoom: (roomId: string) => Promise<void> | void;
  isSuppressed: () => boolean; // XR / editor / measure
}

export class AutoTour {
  private readonly deps: AutoTourDeps;
  private button: HTMLButtonElement | null = null;
  private running = false;
  private rotationRaf: number | null = null;
  private timeoutHandles = new Set<number>();
  private currentIndex = 0;

  constructor(deps: AutoTourDeps) {
    this.deps = deps;
  }

  install(): void {
    this.button = document.getElementById('auto-tour-button') as HTMLButtonElement | null;
    if (!this.button) return;
    this.button.addEventListener('click', () => void this.toggle());

    // Cancel-on-user-input — only while running.
    //
    // The button itself is exempt, and that exemption is what makes ⏹ work at
    // all: this listener is on window in the CAPTURE phase, so a tap on the stop
    // button used to fire pointerdown → stop() before its own click → toggle()
    // saw `running === false` and started the tour over. The visible symptom was
    // "the stop button restarts the tour", with Esc the only way out.
    const cancelOnInput = (e: Event): void => {
      if (!this.running) return;
      const target = e.target;
      if (this.button && target instanceof Node && this.button.contains(target)) return;
      this.stop('user input');
    };
    window.addEventListener('pointerdown', cancelOnInput, { capture: true, passive: true });
    window.addEventListener('wheel', cancelOnInput, { capture: true, passive: true });
    window.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.running) this.stop('escape key');
    });
  }

  async toggle(): Promise<void> {
    if (this.running) {
      this.stop('toggle off');
    } else {
      await this.start();
    }
  }

  // ─────────────────────────────────────────────────────────────────

  private async start(): Promise<void> {
    if (this.deps.isSuppressed()) {
      showToast('Tour cinemático indisponível neste modo', { tone: 'info' });
      return;
    }
    const current = this.deps.getCurrentRoom();
    if (!current) return;

    this.running = true;
    this.currentIndex = this.deps.config.rooms.findIndex((r) => r.id === current.id);
    if (this.currentIndex < 0) this.currentIndex = 0;
    this.renderButton(true);
    showToast('Tour cinemático iniciado — Esc ou toque cancela', { tone: 'info' });
    try {
      localStorage.setItem(TOUR_STORAGE_KEY, '1');
    } catch {
      /* ignore */
    }
    await this.runStep();
  }

  private stop(reason: string): void {
    if (!this.running) return;
    this.running = false;
    if (this.rotationRaf !== null) {
      cancelAnimationFrame(this.rotationRaf);
      this.rotationRaf = null;
    }
    for (const h of this.timeoutHandles) window.clearTimeout(h);
    this.timeoutHandles.clear();
    this.renderButton(false);
    try {
      localStorage.setItem(TOUR_STORAGE_KEY, '0');
    } catch {
      /* ignore */
    }
    console.info(`[auto-tour] stopped: ${reason}`);
  }

  private async runStep(): Promise<void> {
    if (!this.running) return;
    const rooms = this.deps.config.rooms;
    const room = rooms[this.currentIndex];
    if (!room) {
      this.stop('out-of-bounds');
      return;
    }

    // 1. Rotate 360° in the current room.
    await this.rotate360();
    if (!this.running) return;

    // 2. Dwell briefly so the narration can keep speaking if active.
    await this.delay(ROOM_DWELL_MS);
    if (!this.running) return;

    // 2.5 If the narrator is still speaking, wait for it to finish before
    // fading to the next room. Bounded at NARRATION_WAIT_MAX_MS so a stuck
    // SpeechSynthesisUtterance can't hang the tour forever.
    await this.waitForNarrationToFinish();
    if (!this.running) return;

    // 3. Advance to the next room (loop back to 0 at the end).
    this.currentIndex = (this.currentIndex + 1) % rooms.length;
    const next = rooms[this.currentIndex];
    if (!next) {
      this.stop('next not found');
      return;
    }
    await this.deps.navigateToRoom(next.id);
    if (!this.running) return;

    // 4. Recurse.
    await this.runStep();
  }

  private rotate360(): Promise<void> {
    return new Promise((resolve) => {
      if (!this.running) return resolve();
      const startLook = this.deps.controls.getLook();
      const startYaw = startLook.yaw;
      const startedAt = performance.now();
      const step = (now: number): void => {
        if (!this.running) return resolve();
        const t = Math.min(1, (now - startedAt) / ROTATION_DURATION_MS);
        // Ease-in-out so the rotation doesn't snap at the boundaries.
        const eased = 0.5 - 0.5 * Math.cos(t * Math.PI);
        const yaw = startYaw + eased * 360;
        // Pitch slowly returns to 0 across the rotation so the operator's
        // arbitrary starting angle resolves into a horizon view by the end.
        const pitch = startLook.pitch * (1 - eased);
        this.deps.controls.setLook(yaw, pitch);
        if (t < 1) {
          this.rotationRaf = requestAnimationFrame(step);
        } else {
          this.rotationRaf = null;
          resolve();
        }
      };
      this.rotationRaf = requestAnimationFrame(step);
    });
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => {
      const h = window.setTimeout(() => {
        this.timeoutHandles.delete(h);
        resolve();
      }, ms);
      this.timeoutHandles.add(h);
    });
  }

  /**
   * Resolves when `window.speechSynthesis.speaking` becomes false, or when
   * NARRATION_WAIT_MAX_MS elapses (whichever first). No-op if the browser
   * doesn't have speechSynthesis, or if the narrator was never speaking.
   *
   * Polls because there's no event-based equivalent of "speaking changed"
   * — the per-utterance `onend` event fires on the Utterance instance, which
   * Narrator owns, not on the SpeechSynthesis object itself. Polling at
   * 250ms is cheap and matches the operator's perception of "smooth."
   */
  private async waitForNarrationToFinish(): Promise<void> {
    if (!('speechSynthesis' in window)) return;
    if (!window.speechSynthesis.speaking) return;
    const start = performance.now();
    while (window.speechSynthesis.speaking && this.running) {
      if (performance.now() - start > NARRATION_WAIT_MAX_MS) break;
      await this.delay(NARRATION_WAIT_POLL_MS);
    }
  }

  private renderButton(running: boolean): void {
    if (!this.button) return;
    const icon = this.button.querySelector('.icon-button__icon') as HTMLElement | null;
    if (icon) icon.textContent = running ? '⏹️' : '🎬';
    this.button.setAttribute('aria-pressed', String(running));
    this.button.classList.toggle('is-active', running);
    this.button.setAttribute(
      'aria-label',
      running ? 'Parar tour cinemático' : 'Iniciar tour cinemático',
    );
  }
}
